/**
 * The interactive matrix (av-methodology-matrix-ui spec; design D16, D18): a
 * CSS Grid of the 12×3 archetype/relevance cells — preceded by a row of
 * description bands reproducing the historical prototype's `ul#description`
 * (Texto-Imagem / Som / Som-Texto-Imagem-Movimento / +Interatividade /
 * +Mobilidade), with the Y axis rendered Alto (top) → Baixo (bottom) exactly
 * as `docs/art-direction/metodologia-av/index.html`'s `ul#matrixright` did —
 * with a single overlaid SVG layer for ContentNodes (squares), InfluenceNodes
 * (circles, inheriting their content's color), the dashed Content↔Influence
 * lines (derived, not stored), and solid Relationship lines (explicit,
 * content↔content).
 *
 * Both node types are draggable via Pointer Events, independently of each
 * other, and repositionable via arrow keys when focused (keyboard
 * alternative to dragging). Semantic position is always the source of
 * truth — SVG (x, y) is computed from it, never the other way around
 * (matrix-geometry.ts).
 */

import { archetypes, methodology } from "../domain/data-source";
import { referenceLanguageForArchetype } from "../domain/reference-language";
import type { ArchetypeId, ContentNode, InfluenceNode, MatrixPositionId, RelevanceId } from "../domain/types";
import {
  colorForContent,
  loadContentNodes,
  loadInfluenceNodes,
  loadRelationships,
  saveContentNode,
  saveInfluenceNode,
} from "../state/content-store";
import {
  centerOf,
  pointToNearestCell,
  radiusForWeight,
  stackedYOffsets,
  type CellRect,
} from "./matrix-geometry";

const SVG_NS = "http://www.w3.org/2000/svg";

export type Selection = { type: "content"; id: string } | { type: "influence"; id: string } | null;

/**
 * Resolves the current selection to the ContentNode the results panel
 * should show. Selecting an InfluenceNode resolves to ITS PARENT content —
 * an Influence is never independently classified/recommended (spec:
 * av-methodology-classification), so treating an influence selection as
 * "nothing to show" previously left the results panel blank right after
 * creating or dragging an influence, even though the parent content's
 * hybridization had, in fact, just updated. This is the fix.
 */
export function resolveContentFromSelection(selection: Selection): ContentNode | null {
  if (!selection) return null;
  if (selection.type === "content") {
    return loadContentNodes().find((c) => c.id === selection.id) ?? null;
  }
  const influence = loadInfluenceNodes().find((i) => i.id === selection.id);
  if (!influence) return null;
  return loadContentNodes().find((c) => c.id === influence.contentId) ?? null;
}

export interface MatrixController {
  refresh(): void;
}

export interface MatrixCallbacks {
  onSelect: (selection: Selection) => void;
  /** Cell (empty area) activated — create a new content exactly here. */
  onCellActivate: (position: { archetypeId: ArchetypeId; relevance: RelevanceId }) => void;
  /** The in-matrix "+" glyph on a selected content was activated. */
  onCreateInfluence: (contentId: string) => void;
  /** Double-click / edit gesture on a content: rename and/or reweight. */
  onEditContent: (contentId: string) => void;
  /** Double-click / edit gesture on an influence: reweight (its size). */
  onEditInfluence: (influenceId: string) => void;
  /** Delete key while a content is focused — cascades to its influences. */
  onDeleteContent: (contentId: string) => void;
  /** Delete key while an influence is focused. */
  onDeleteInfluence: (influenceId: string) => void;
}

// Description bands reproducing docs/art-direction/metodologia-av/index.html's
// ul#description verbatim (text and archetype-order spans), per the author's
// explicit instruction to recover this element. orderStart/orderEnd are
// 1-indexed archetype `order` values (inclusive).
const DESCRIPTION_BANDS: Array<{
  text: string;
  sub?: string;
  /** Author-requested deviation from the historical `.matrix-description-sub`
   *  default (italic) — a sub-label reading as a range indicator (-/+ with
   *  arrows), not a plain caption, renders upright instead (design D34/D35). */
  subItalic?: boolean;
  orderStart: number;
  orderEnd: number;
}> = [
  {
    text: "Texto - Imagem",
    // Author-requested wording (deviates from the historical prototype's
    // literal "(Abrangência do número de leitores e do conteúdo)"): framed
    // as a range from "-" to "+", the concept growing in both directions
    // away from the bracketed label.
    sub: "- <-- [Abrangência do número de leitores e do conteúdo] --> +",
    subItalic: false,
    orderStart: 1,
    orderEnd: 6,
  },
  { text: "Som", orderStart: 7, orderEnd: 7 },
  {
    text: "Som - Texto - Imagem - Imagem em movimento",
    // Author-requested wording (deviates from the historical prototype's
    // literal "(Qualidade de produção)"): same range-indicator framing as
    // the first band's sub-label (design D34/D35).
    sub: "- <-- [Qualidade de produção] --> +",
    subItalic: false,
    orderStart: 8,
    orderEnd: 10,
  },
  { text: "Som - Texto - Imagem - Imagem em movimento - Interatividade", orderStart: 11, orderEnd: 11 },
  {
    text: "Som - Texto - Imagem - Imagem em movimento - Interatividade - Mobilidade",
    orderStart: 12,
    orderEnd: 12,
  },
];

const orderedArchetypes = () => [...archetypes].sort((a, b) => a.order - b.order);
const relevanceLevels = () => methodology.dimensions.relevance.levels; // low, medium, high (value 1,2,3)

function archetypeByOrder(order: number): ArchetypeId | undefined {
  return orderedArchetypes().find((a) => a.order === order)?.id;
}

function relevanceByValue(value: number): RelevanceId | undefined {
  return relevanceLevels().find((l) => l.value === value)?.id;
}

function squareSizeForWeight(weight: 1 | 2 | 3): number {
  return radiusForWeight(weight) * 1.8; // half-width; square drawn as size×size centered
}

// Grid row layout: row 1 = description bands, row 2 = column/corner headers,
// rows 3–5 = data rows. Y axis renders Alto (value 3) at the TOP data row and
// Baixo (value 1) at the BOTTOM — matching index.html's `ul#matrixright`
// ("Alto" listed first, "Baixo" last), not the low-to-high value order the
// domain data itself uses internally.
function rowForRelevanceValue(value: number): number {
  return 6 - value; // value 3 -> row 3 (top), value 1 -> row 5 (bottom)
}

/**
 * Maps every node in `nodes` (ContentNodes, or InfluenceNodes — anything
 * with a matrix position) to its SVG point, spreading nodes that share the
 * same cell vertically instead of overlapping them (matrix-geometry.ts's
 * `stackedYOffsets`; reported by the author: several contents in one cell
 * used to render exactly on top of each other). Grouping is done per call
 * in `nodes`' own order, which is stable across redraws (content-store
 * preserves array position on edit — see content-store.ts's
 * `saveContentNode`), so a node doesn't jump to a different stack slot just
 * because an unrelated field changed.
 */
function pointsForCell<T extends MatrixPositionId & { id: string }>(
  nodes: T[],
  cellRects: CellRect[],
): Map<string, { x: number; y: number }> {
  const points = new Map<string, { x: number; y: number }>();
  const groups = new Map<string, T[]>();
  for (const node of nodes) {
    const key = `${node.archetypeId}:${node.relevance}`;
    const group = groups.get(key);
    if (group) group.push(node);
    else groups.set(key, [node]);
  }
  for (const [key, group] of groups) {
    const [archetypeId, relevance] = key.split(":") as [ArchetypeId, RelevanceId];
    const cell = cellRects.find((c) => c.archetypeId === archetypeId && c.relevance === relevance);
    if (!cell) continue;
    const center = centerOf(cell);
    const offsets = stackedYOffsets(cell.height, group.length);
    group.forEach((node, i) => {
      points.set(node.id, { x: center.x, y: center.y + offsets[i] });
    });
  }
  return points;
}

export function renderMatrix(container: HTMLElement, callbacks: MatrixCallbacks): MatrixController {
  container.innerHTML = "";

  let selection: Selection = null;

  const shell = document.createElement("div");
  shell.className = "matrix-shell";
  container.appendChild(shell);

  const grid = document.createElement("div");
  grid.className = "matrix-grid";
  grid.setAttribute("role", "grid");
  grid.setAttribute(
    "aria-label",
    `Matriz da Metodologia AV — ${methodology.dimensions.relevance.label} por posição`,
  );
  shell.appendChild(grid);

  // Description bands (row 1) — reproduces ul#description.
  for (const band of DESCRIPTION_BANDS) {
    const bandEl = document.createElement("div");
    bandEl.className = "matrix-description-band";
    bandEl.style.gridColumn = `${band.orderStart + 1} / ${band.orderEnd + 2}`;
    bandEl.style.gridRow = "1";
    bandEl.dataset.orderStart = String(band.orderStart);
    bandEl.dataset.orderEnd = String(band.orderEnd);
    const text = document.createElement("span");
    text.textContent = band.text;
    bandEl.appendChild(text);
    if (band.sub) {
      const sub = document.createElement("span");
      sub.className = "matrix-description-sub";
      if (band.subItalic === false) sub.classList.add("matrix-description-sub--upright");
      sub.textContent = band.sub;
      bandEl.appendChild(sub);
    }
    grid.appendChild(bandEl);
  }

  const corner = document.createElement("div");
  corner.className = "matrix-corner";
  corner.style.gridRow = "2";
  corner.textContent = methodology.dimensions.relevance.label;
  grid.appendChild(corner);

  // Design D38 (author reversal of D29/D36/D37): column headers show the
  // reference-language label (Documento, Livro, ..., Celular) again, as
  // visible text — read from the same interpretation-layer catalog the
  // results panel already uses (reference-languages.json, via
  // reference-language.ts), not a second hardcoded copy of the twelve
  // names. The aria-label is unaffected by this — it still says "Posição
  // N", never the reference-language name — D29's accessible-label
  // decision stands; only the header's *visible* text changed.
  for (const archetype of orderedArchetypes()) {
    const header = document.createElement("div");
    header.className = "matrix-col-header";
    header.style.gridColumn = String(archetype.order + 1);
    header.style.gridRow = "2";
    header.dataset.order = String(archetype.order);
    header.textContent = referenceLanguageForArchetype(archetype.id)?.label ?? "";
    header.setAttribute("aria-label", `Posição ${archetype.order}`);
    grid.appendChild(header);
  }

  for (const level of relevanceLevels()) {
    const header = document.createElement("div");
    header.className = "matrix-row-header";
    header.style.gridColumn = "1";
    header.style.gridRow = String(rowForRelevanceValue(level.value));
    header.textContent = level.label;
    grid.appendChild(header);
  }

  const cellElements = new Map<string, HTMLElement>();
  for (const archetype of archetypes) {
    for (const level of relevanceLevels()) {
      const cell = document.createElement("div");
      cell.className = "matrix-cell";
      cell.style.gridColumn = String(archetype.order + 1);
      cell.style.gridRow = String(rowForRelevanceValue(level.value));
      cell.dataset.archetypeId = archetype.id;
      cell.dataset.relevance = level.id;
      cell.dataset.order = String(archetype.order);
      cell.setAttribute("role", "gridcell");
      cell.setAttribute("tabindex", "0");
      cell.setAttribute(
        "aria-label",
        `Posição ${archetype.order}, ${methodology.dimensions.relevance.label.toLowerCase()} ${level.label.toLowerCase()}. Pressione Enter para criar um conteúdo aqui.`,
      );
      const activate = () => callbacks.onCellActivate({ archetypeId: archetype.id, relevance: level.id });
      cell.addEventListener("click", activate);
      cell.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate();
        }
      });
      // Hover highlights only this cell — matching the historical prototype's
      // `ul#matrix li:hover { background-color: #f1f1f1; }` exactly (design
      // D18 correction: an earlier round highlighted the whole column, which
      // the author confirmed was not what was wanted). Handled entirely by
      // the plain CSS `:hover` rule on `.matrix-cell` — no JS needed.
      grid.appendChild(cell);
      cellElements.set(`${archetype.id}:${level.id}`, cell);
    }
  }

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.classList.add("matrix-svg");
  // "none" (not the SVG default "xMidYMid meet"): on screen this has no
  // effect (draw() pixel-locks the rendered box to exactly match the
  // viewBox, so there's never a size mismatch to resolve — see the note in
  // draw() below). It matters for print (design D22 follow-up): the
  // beforeprint-triggered redraw is not reliably reflected in every
  // browser's print-preview snapshot, so print CSS instead forces this SVG
  // back to a plain 100%/100% box (styles.css's @media print) — with
  // "none", the last-computed viewBox content stretches non-uniformly to
  // fill whatever box the print layout actually gives it, rather than
  // "meet"'s letterboxed uniform scale (which would leave the content
  // sitting in the wrong place relative to the print table entirely).
  svg.setAttribute("preserveAspectRatio", "none");
  shell.appendChild(svg);

  function computeCellRects(): CellRect[] {
    const gridRect = grid.getBoundingClientRect();
    const rects: CellRect[] = [];
    for (const [key, el] of cellElements) {
      const [archetypeId, relevance] = key.split(":") as [ArchetypeId, RelevanceId];
      const r = el.getBoundingClientRect();
      rects.push({
        archetypeId,
        relevance,
        x: r.left - gridRect.left,
        y: r.top - gridRect.top,
        width: r.width,
        height: r.height,
      });
    }
    return rects;
  }

  function relevanceLabel(id: RelevanceId): string {
    return relevanceLevels().find((l) => l.id === id)?.label ?? id;
  }

  // Design D29: returns "Posição N" (column number), never the archetype's
  // reference-language label — accessible labels for nodes rendered INSIDE
  // the matrix follow the same rule as the visual column headers.
  function positionLabel(id: ArchetypeId): string {
    const order = archetypes.find((a) => a.id === id)?.order;
    return `posição ${order ?? id}`;
  }

  function weightLabel(weight: 1 | 2 | 3): string {
    return methodology.contentWeight.levels.find((w) => w.value === weight)?.label ?? String(weight);
  }

  function contentAccessibleLabel(content: ContentNode): string {
    return `Conteúdo: ${content.label} — ${positionLabel(content.archetypeId)}, ${methodology.dimensions.relevance.label.toLowerCase()} ${relevanceLabel(content.relevance).toLowerCase()}, peso ${weightLabel(content.weight).toLowerCase()}`;
  }

  function influenceAccessibleLabel(influence: InfluenceNode, parentLabel: string): string {
    return `Influência de: ${parentLabel} — ${positionLabel(influence.archetypeId)}, ${methodology.dimensions.relevance.label.toLowerCase()} ${relevanceLabel(influence.relevance).toLowerCase()}, peso ${weightLabel(influence.weight).toLowerCase()}`;
  }

  function moveByArrowKey(position: { archetypeId: ArchetypeId; relevance: RelevanceId }, key: string) {
    const archetype = archetypes.find((a) => a.id === position.archetypeId)!;
    const level = relevanceLevels().find((l) => l.id === position.relevance)!;
    let order: number = archetype.order;
    let value: number = level.value;
    if (key === "ArrowLeft") order = Math.max(1, order - 1);
    if (key === "ArrowRight") order = Math.min(12, order + 1);
    if (key === "ArrowUp") value = Math.min(3, value + 1);
    if (key === "ArrowDown") value = Math.max(1, value - 1);
    const newArchetypeId = archetypeByOrder(order);
    const newRelevance = relevanceByValue(value);
    if (!newArchetypeId || !newRelevance) return null;
    return { archetypeId: newArchetypeId, relevance: newRelevance };
  }

  function select(next: Selection): void {
    selection = next;
    callbacks.onSelect(next);
    draw();
  }

  function draw(): void {
    const cellRects = computeCellRects();
    // Bug fix: `.matrix-shell` scrolls horizontally (the grid's min-width
    // exceeds the visible container), but the SVG overlay's CSS previously
    // set `width: 100%` — 100% of the *visible* shell, not of the wider
    // scrollable grid. That mismatch made the browser rescale the SVG's
    // viewBox coordinate space to fit the narrower rendered box, so every
    // node drew at a horizontally compressed position — increasingly off
    // the further right (and the further the matrix scrolled), which looked
    // like "content appears in the cell next to the one clicked." Setting
    // the SVG's actual pixel width/height to match the grid's real content
    // size (not a percentage) keeps 1 viewBox unit == 1 screen pixel, so
    // computeCellRects()'s getBoundingClientRect()-based coordinates line
    // up with what's drawn exactly, regardless of scroll/overflow.
    const gridWidth = grid.scrollWidth;
    const gridHeight = grid.scrollHeight;
    svg.setAttribute("viewBox", `0 0 ${gridWidth} ${gridHeight}`);
    svg.style.width = `${gridWidth}px`;
    svg.style.height = `${gridHeight}px`;
    svg.innerHTML = "";

    // Bug fix: draw() rebuilds every SVG node from scratch on each call, so
    // whichever element previously held keyboard focus (e.g. right after a
    // click selected it) is destroyed and replaced — focus does NOT carry
    // over to its replacement automatically. Arrow-key movement and Delete
    // both depend on the node having actual DOM focus, so without this,
    // selecting a node with the mouse leaves keyboard shortcuts dead: the
    // click *looked* like it selected something (the `.is-selected` style
    // applied), but no element actually had focus to receive the keydown.
    // Track the selected node's freshly-created element and refocus it once
    // the redraw is done.
    let selectedElement: SVGGraphicsElement | null = null;

    const contents = loadContentNodes();
    const influences = loadInfluenceNodes();
    const relationships = loadRelationships();

    const contentPoint = pointsForCell(contents, cellRects);
    const influencePoint = pointsForCell(influences, cellRects);

    // Solid lines: explicit Relationships (content ↔ content).
    for (const rel of relationships) {
      const from = contentPoint.get(rel.sourceContentId);
      const to = contentPoint.get(rel.targetContentId);
      if (!from || !to) continue;
      const line = document.createElementNS(SVG_NS, "line");
      line.setAttribute("x1", String(from.x));
      line.setAttribute("y1", String(from.y));
      line.setAttribute("x2", String(to.x));
      line.setAttribute("y2", String(to.y));
      line.classList.add("relationship-line");
      svg.appendChild(line);
    }

    // Dashed lines: derived automatically, Content ↔ its own Influences.
    for (const influence of influences) {
      const contentPt = contentPoint.get(influence.contentId);
      const influencePt = influencePoint.get(influence.id);
      if (!contentPt || !influencePt) continue;
      const line = document.createElementNS(SVG_NS, "line");
      line.setAttribute("x1", String(contentPt.x));
      line.setAttribute("y1", String(contentPt.y));
      line.setAttribute("x2", String(influencePt.x));
      line.setAttribute("y2", String(influencePt.y));
      line.classList.add("influence-line");
      svg.appendChild(line);
    }

    // Content squares.
    for (const content of contents) {
      const point = contentPoint.get(content.id);
      if (!point) continue;
      const size = squareSizeForWeight(content.weight);
      const isSelected = selection?.type === "content" && selection.id === content.id;

      const rect = document.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", String(point.x - size / 2));
      rect.setAttribute("y", String(point.y - size / 2));
      rect.setAttribute("width", String(size));
      rect.setAttribute("height", String(size));
      rect.setAttribute("rx", "4");
      rect.style.stroke = content.color;
      rect.style.fill = content.color;
      rect.style.fillOpacity = isSelected ? "0.32" : "0.18";
      rect.classList.add("content-node");
      if (isSelected) rect.classList.add("is-selected");
      rect.setAttribute("tabindex", "0");
      rect.setAttribute("role", "button");
      rect.setAttribute("aria-label", contentAccessibleLabel(content));
      rect.dataset.contentId = content.id;

      const label = document.createElementNS(SVG_NS, "text");
      label.setAttribute("x", String(point.x));
      label.setAttribute("y", String(point.y + size / 2 + 12));
      label.classList.add("content-node-label");
      label.textContent = content.label;

      attachDragHandlers(rect, cellRects, (cell) => {
        saveContentNode({ ...content, archetypeId: cell.archetypeId, relevance: cell.relevance });
        select({ type: "content", id: content.id });
      });
      attachKeyboardHandlers(rect, content, {
        onMove: (newPos) => {
          saveContentNode({ ...content, ...newPos });
          select({ type: "content", id: content.id });
        },
        onDelete: () => callbacks.onDeleteContent(content.id),
      });
      rect.addEventListener("click", () => select({ type: "content", id: content.id }));
      rect.addEventListener("dblclick", (event) => {
        event.preventDefault();
        callbacks.onEditContent(content.id);
      });

      svg.appendChild(rect);
      svg.appendChild(label);
      if (isSelected) selectedElement = rect;

      // In-matrix "+" glyph: the ONLY way to start creating an influence,
      // replacing the removed below-table "+ Influência" button. Only drawn
      // on the currently selected content (author's explicit interaction spec).
      if (isSelected) {
        const plusGroup = document.createElementNS(SVG_NS, "g");
        const plusX = point.x + size / 2 + 2;
        const plusY = point.y - size / 2 - 2;
        const plusCircle = document.createElementNS(SVG_NS, "circle");
        plusCircle.setAttribute("cx", String(plusX));
        plusCircle.setAttribute("cy", String(plusY));
        plusCircle.setAttribute("r", "9");
        plusCircle.classList.add("add-influence-glyph");
        plusCircle.setAttribute("role", "button");
        plusCircle.setAttribute("tabindex", "0");
        plusCircle.setAttribute("aria-label", `Adicionar influência a ${content.label}`);
        const plusLabel = document.createElementNS(SVG_NS, "text");
        plusLabel.setAttribute("x", String(plusX));
        plusLabel.setAttribute("y", String(plusY + 4));
        plusLabel.classList.add("add-influence-glyph-label");
        plusLabel.textContent = "+";
        const activateAdd = (event: Event) => {
          event.stopPropagation();
          callbacks.onCreateInfluence(content.id);
        };
        plusCircle.addEventListener("click", activateAdd);
        plusCircle.addEventListener("keydown", (event) => {
          if ((event as KeyboardEvent).key === "Enter" || (event as KeyboardEvent).key === " ") {
            event.preventDefault();
            activateAdd(event);
          }
        });
        plusGroup.append(plusCircle, plusLabel);
        svg.appendChild(plusGroup);
      }
    }

    // Influence circles.
    for (const influence of influences) {
      const point = influencePoint.get(influence.id);
      if (!point) continue;
      const parent = contents.find((c) => c.id === influence.contentId);
      const color = colorForContent(influence.contentId) ?? "#999";
      const isSelected = selection?.type === "influence" && selection.id === influence.id;

      const circle = document.createElementNS(SVG_NS, "circle");
      circle.setAttribute("cx", String(point.x));
      circle.setAttribute("cy", String(point.y));
      circle.setAttribute("r", String(radiusForWeight(influence.weight)));
      circle.style.fill = color;
      circle.classList.add("influence-node");
      if (isSelected) circle.classList.add("is-selected");
      circle.setAttribute("tabindex", "0");
      circle.setAttribute("role", "button");
      circle.setAttribute("aria-label", influenceAccessibleLabel(influence, parent?.label ?? "conteúdo"));
      circle.dataset.influenceId = influence.id;

      attachDragHandlers(circle, cellRects, (cell) => {
        saveInfluenceNode({ ...influence, archetypeId: cell.archetypeId, relevance: cell.relevance });
        select({ type: "influence", id: influence.id });
      });
      attachKeyboardHandlers(circle, influence, {
        onMove: (newPos) => {
          saveInfluenceNode({ ...influence, ...newPos });
          select({ type: "influence", id: influence.id });
        },
        onDelete: () => callbacks.onDeleteInfluence(influence.id),
      });
      circle.addEventListener("click", () => select({ type: "influence", id: influence.id }));
      circle.addEventListener("dblclick", (event) => {
        event.preventDefault();
        callbacks.onEditInfluence(influence.id);
      });

      svg.appendChild(circle);
      if (isSelected) selectedElement = circle;

      // Edit glyph: appears on the selected influence, same pattern as the
      // "+" glyph on a selected content — a single click opens the weight
      // prompt, no double-click needed to discover it. Double-click remains
      // a working shortcut too (kept, not removed).
      if (isSelected) {
        const editRadius = radiusForWeight(influence.weight);
        const editX = point.x + editRadius + 2;
        const editY = point.y - editRadius - 2;
        const editGroup = document.createElementNS(SVG_NS, "g");
        const editGlyph = document.createElementNS(SVG_NS, "circle");
        editGlyph.setAttribute("cx", String(editX));
        editGlyph.setAttribute("cy", String(editY));
        editGlyph.setAttribute("r", "9");
        editGlyph.classList.add("edit-influence-glyph");
        editGlyph.setAttribute("role", "button");
        editGlyph.setAttribute("tabindex", "0");
        editGlyph.setAttribute("aria-label", "Editar peso da influência");
        const editLabel = document.createElementNS(SVG_NS, "text");
        editLabel.setAttribute("x", String(editX));
        editLabel.setAttribute("y", String(editY + 3));
        editLabel.classList.add("edit-influence-glyph-label");
        editLabel.textContent = "✎";
        const activateEdit = (event: Event) => {
          event.stopPropagation();
          callbacks.onEditInfluence(influence.id);
        };
        editGlyph.addEventListener("click", activateEdit);
        editGlyph.addEventListener("keydown", (event) => {
          if ((event as KeyboardEvent).key === "Enter" || (event as KeyboardEvent).key === " ") {
            event.preventDefault();
            activateEdit(event);
          }
        });
        editGroup.append(editGlyph, editLabel);
        svg.appendChild(editGroup);
      }
    }

    // Restore keyboard focus to the (freshly-rebuilt) selected element, so
    // arrow-key movement and Delete/Backspace keep working after this
    // redraw — see the note above `selectedElement`'s declaration.
    selectedElement?.focus({ preventScroll: true });
  }

  function attachDragHandlers(
    el: SVGGraphicsElement,
    cellRects: CellRect[],
    onDrop: (cell: CellRect) => void,
  ): void {
    let dragging = false;
    let moved = false;

    el.addEventListener("pointerdown", (event) => {
      dragging = true;
      moved = false;
      (el as unknown as Element).setPointerCapture((event as PointerEvent).pointerId);
      el.dataset.dragging = "true";
    });

    el.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      moved = true;
      const pe = event as PointerEvent;
      const gridRect = grid.getBoundingClientRect();
      const x = pe.clientX - gridRect.left;
      const y = pe.clientY - gridRect.top;
      if (el.tagName === "circle") {
        el.setAttribute("cx", String(x));
        el.setAttribute("cy", String(y));
      } else if (el.tagName === "rect") {
        const w = Number(el.getAttribute("width"));
        const h = Number(el.getAttribute("height"));
        el.setAttribute("x", String(x - w / 2));
        el.setAttribute("y", String(y - h / 2));
      }
    });

    const endDrag = (event: Event) => {
      if (!dragging) return;
      dragging = false;
      el.dataset.dragging = "false";
      if (!moved) return; // a plain click, not a drag — let the click handler do the selecting
      const pe = event as PointerEvent;
      const gridRect = grid.getBoundingClientRect();
      const dropPoint = { x: pe.clientX - gridRect.left, y: pe.clientY - gridRect.top };
      const nearestCell = pointToNearestCell(dropPoint, cellRects);
      if (nearestCell) onDrop(nearestCell);
    };

    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);
  }

  function attachKeyboardHandlers(
    el: SVGGraphicsElement,
    node: { archetypeId: ArchetypeId; relevance: RelevanceId },
    handlers: {
      onMove: (newPos: { archetypeId: ArchetypeId; relevance: RelevanceId }) => void;
      onDelete: () => void;
    },
  ): void {
    el.addEventListener("keydown", (event) => {
      const ke = event as KeyboardEvent;
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(ke.key)) {
        ke.preventDefault();
        const newPos = moveByArrowKey(node, ke.key);
        if (newPos) handlers.onMove(newPos);
      } else if (ke.key === "Delete" || ke.key === "Backspace") {
        ke.preventDefault();
        handlers.onDelete();
      }
    });
  }

  draw();
  window.addEventListener("resize", draw);

  return { refresh: draw };
}
